import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DateRangeFilter = ({ onDateRangeChange, className = '' }) => {
  const [selectedRange, setSelectedRange] = useState('week');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [showCustom, setShowCustom] = useState(false);

  const presetRanges = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'quarter', label: 'This Quarter' },
    { key: 'custom', label: 'Custom Range' }
  ];

  const getDateRange = (range) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // helpers to normalize to start/end of day
    const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
    const endOfDay = (d) => { const x = new Date(d); x.setHours(23,59,59,999); return x; };

    switch (range) {
      case 'today': {
        return { from: startOfDay(today), to: endOfDay(today) };
      }
      case 'week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return { from: startOfDay(weekStart), to: endOfDay(weekEnd) };
      }
      case 'month': {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { from: startOfDay(monthStart), to: endOfDay(monthEnd) };
      }
      case 'quarter': {
        const quarter = Math.floor(today.getMonth() / 3);
        const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
        const quarterEnd = new Date(today.getFullYear(), quarter * 3 + 3, 0);
        return { from: startOfDay(quarterStart), to: endOfDay(quarterEnd) };
      }
      default:
        return null;
    }
  };

  const handleRangeSelect = (range) => {
    setSelectedRange(range);
    
    if (range === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      const dateRange = getDateRange(range);
      if (dateRange && onDateRangeChange) {
        onDateRangeChange(dateRange);
      }
    }
  };

  const handleCustomRangeApply = () => {
    if (customRange?.from && customRange?.to && onDateRangeChange) {
      const from = new Date(customRange.from);
      const to = new Date(customRange.to);
      from.setHours(0,0,0,0);
      to.setHours(23,59,59,999);
      onDateRangeChange({ from, to });
    }
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getCurrentRangeDisplay = () => {
    if (selectedRange === 'custom' && customRange?.from && customRange?.to) {
      return `${formatDateForDisplay(new Date(customRange.from))} - ${formatDateForDisplay(new Date(customRange.to))}`;
    }
    
    const range = getDateRange(selectedRange);
    if (range) {
      return `${formatDateForDisplay(range?.from)} - ${formatDateForDisplay(range?.to)}`;
    }
    
    return 'Select date range';
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h4 className="text-sm font-medium text-foreground mb-1">Date Range</h4>
          <p className="text-xs text-muted-foreground">{getCurrentRangeDisplay()}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {presetRanges?.map((range) => (
            <Button
              key={range?.key}
              variant={selectedRange === range?.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleRangeSelect(range?.key)}
            >
              {range?.label}
            </Button>
          ))}
        </div>
      </div>
      {showCustom && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <Input
              type="date"
              label="From Date"
              value={customRange?.from}
              onChange={(e) => setCustomRange(prev => ({ ...prev, from: e?.target?.value }))}
            />
            <Input
              type="date"
              label="To Date"
              value={customRange?.to}
              onChange={(e) => setCustomRange(prev => ({ ...prev, to: e?.target?.value }))}
            />
            <Button
              variant="default"
              onClick={handleCustomRangeApply}
              disabled={!customRange?.from || !customRange?.to}
              iconName="Check"
              iconPosition="left"
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
